import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Briefcase, MapPin, Clock, ArrowRight, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SectionDecor } from "@/components/Decor";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const OFFERS = [
  {
    id: "animateur",
    title: "Animateur·rice numérique",
    type: "Freelance / Temps partiel",
    location: "Bruxelles & Wallonie",
    desc: "Vous animez nos ateliers et stages auprès des enfants (3e à 6e primaire), avec pédagogie et enthousiasme. Formation interne assurée.",
  },
  {
    id: "coordinateur",
    title: "Coordinateur·rice de stages",
    type: "CDD / Temps plein",
    location: "Bruxelles",
    desc: "Vous organisez la logistique des stages de vacances : plannings, animateurs, matériel et relation avec les familles.",
  },
];

const POSITIONS = [...OFFERS.map((o) => o.title), "Candidature spontanée"];

export default function Job() {
  const formRef = useRef(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", position: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [cv, setCv] = useState(null);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const applyTo = (title) => {
    setForm((f) => ({ ...f, position: title }));
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.position) { toast.error("Choisissez un poste"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("phone", form.phone || "");
      fd.append("position", form.position);
      fd.append("message", form.message || "");
      if (cv) fd.append("cv", cv);
      await api.post("/candidature", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Candidature envoyée ! Nous revenons vers vous rapidement.");
      setForm({ name: "", email: "", phone: "", position: "", message: "" });
      setCv(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="job-page">
      <section className="relative overflow-hidden bg-gradient-to-b from-sky-50 to-white py-16">
        <SectionDecor />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-sky-500">Contact · Job</span>
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-slate-900 mt-2">
            REJOIGNEZ KWARIZMIX
          </h1>
          <p className="mt-4 text-lg text-slate-600 max-w-2xl">
            Envie de transmettre la passion du numérique aux plus jeunes ? Découvrez nos offres
            et postulez dès aujourd'hui — formation à notre pédagogie assurée.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid md:grid-cols-3 gap-6">
        {OFFERS.map((o, i) => (
          <motion.div
            key={o.id}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: (i % 3) * 0.08 }}
            className="bg-white rounded-3xl border border-slate-100 shadow-sm p-7 flex flex-col"
            data-testid={`job-offer-${o.id}`}
          >
            <span className="grid place-items-center w-14 h-14 rounded-2xl bg-sky-100 text-sky-600 mb-4">
              <Briefcase className="w-7 h-7" />
            </span>
            <h3 className="font-heading font-extrabold text-xl text-slate-900">{o.title}</h3>
            <div className="flex flex-wrap gap-3 mt-3 text-sm text-slate-500">
              <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4 text-sky-500" /> {o.type}</span>
              <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-sky-500" /> {o.location}</span>
            </div>
            <p className="text-sm text-slate-500 mt-4 flex-1">{o.desc}</p>
            <button onClick={() => applyTo(o.title)} data-testid={`job-apply-${o.id}`} className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-full bg-slate-900 text-white font-bold hover:bg-sky-600 transition-colors w-fit">
              Postuler <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
        ))}
      </section>

      {/* Formulaire de candidature */}
      <section ref={formRef} id="candidature" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 scroll-mt-24">
        <div className="bg-white rounded-[2rem] border border-slate-100 shadow-sm p-8 sm:p-10">
          <h2 className="font-heading font-black text-2xl sm:text-3xl text-slate-900">Postuler chez Kwarizmix</h2>
          <p className="text-slate-500 mt-2">Remplissez le formulaire ci-dessous. Vous pourrez nous transmettre votre CV par email en réponse à notre accusé de réception.</p>
          <form onSubmit={submit} className="space-y-4 mt-8" data-testid="job-application-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Nom complet *</Label>
                <Input required value={form.name} onChange={update("name")} className="rounded-xl mt-1" data-testid="job-name" />
              </div>
              <div>
                <Label>Email *</Label>
                <Input required type="email" value={form.email} onChange={update("email")} className="rounded-xl mt-1" data-testid="job-email" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Téléphone</Label>
                <Input value={form.phone} onChange={update("phone")} className="rounded-xl mt-1" data-testid="job-phone" />
              </div>
              <div>
                <Label>Poste visé *</Label>
                <Select value={form.position} onValueChange={(v) => setForm((f) => ({ ...f, position: v }))}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="job-position"><SelectValue placeholder="Choisir un poste" /></SelectTrigger>
                  <SelectContent>
                    {POSITIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Message / motivation *</Label>
              <Textarea required rows={5} value={form.message} onChange={update("message")} className="rounded-xl mt-1" data-testid="job-message" placeholder="Parlez-nous de vous, de votre expérience avec les enfants et de vos disponibilités." />
            </div>
            <div>
              <Label>CV (PDF ou Word — optionnel)</Label>
              <input type="file" accept=".pdf,.doc,.docx" onChange={(e) => setCv(e.target.files?.[0] || null)} data-testid="job-cv" className="mt-1 block w-full text-sm text-slate-600 file:mr-4 file:rounded-full file:border-0 file:bg-sky-100 file:px-4 file:py-2 file:text-sky-700 file:font-bold hover:file:bg-sky-200 cursor-pointer" />
              {cv && <p className="text-xs text-slate-400 mt-1">Fichier sélectionné : {cv.name}</p>}
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="job-submit"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-sky-500 text-white font-bold hover:bg-sky-600 hover:scale-105 transition-[transform,background-color] disabled:opacity-60"
            >
              <Send className="w-5 h-5" /> {loading ? "Envoi..." : "Envoyer ma candidature"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
